import { PutPostUtilityItem, ResidualWasteContract } from "@/api/facilities";
import { RootInput, RootInputNumber } from "@/components/BaseComponents/Inputs";
import ImpactPill from "@/components/Impact/ImpactPill";
import ImpactReferenceSelector from "@/components/ImpactReferenceSelector/ImpactReferenceSelector";
import RootModal from "@/components/RootModal/RootModal";
import useAnalytics from "@/hooks/useAnalytics";
import { EcoInventDataset, ImpactDataset } from "@/hooks/useEcoInventDatasets";
import {
  useCreateResidualWaste,
  useGetResidualWaste,
  useUpdateResidualWaste,
} from "@/hooks/useFacilities";
import { multiplyImpactWithFactor } from "@/pages/facilities/detail/Utilities/utils";
import usePublishedId from "@/pages/facilities/hooks/usePublishedId";
import { Form, message, Space } from "antd";
import { useWatch } from "antd/es/form/Form";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

export default function ResidualWasteModal({
  facilityId,
  open,
  closeModal,
  editContract: contract,
  minDate,
  maxDate,
}: {
  facilityId: number;
  open: boolean;
  closeModal: () => void;
  editContract?: ResidualWasteContract;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
}) {
  const publishedId = usePublishedId();
  const [form] = Form.useForm();
  const { data, isFetching } = useGetResidualWaste(contract?.id || 1, !!contract);
  const createResidualWaste = useCreateResidualWaste();
  const updateResidualWaste = useUpdateResidualWaste();
  const analytics = useAnalytics({ enabled: !publishedId });

  const [selectedDataset, setSelectedDataset] = useState<ImpactDataset | EcoInventDataset>();
  const [confirmedDataset, setConfirmedDataset] = useState<ImpactDataset>();
  const amount = useWatch("amount", form);

  useEffect(() => {
    if (data) {
      setConfirmedDataset(data?.match);
      form.setFieldsValue({
        name: data.name,
        amount: data.amount,
      });
    }
  }, [data]);

  const onDatasetConfirm = () => {
    setConfirmedDataset(selectedDataset);
    if (selectedDataset) {
      if ("activity_name" in selectedDataset) {
        setConfirmedDataset({ ...selectedDataset, name: selectedDataset.activity_name });
      } else {
        setConfirmedDataset(selectedDataset);
      }
    }
    form.validateFields(["reference"]);
  };

  const onDatasetChange = (dataset?: ImpactDataset) => {
    setSelectedDataset(dataset);
  };

  const onDelete = () => {
    setConfirmedDataset(undefined);
    form.validateFields(["reference"]);
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (confirmedDataset) {
          const contractToPutPost: PutPostUtilityItem = {
            facility_id: facilityId,
            name: values.name,
            amount: values.amount,
            start_date: minDate?.format("YYYY-MM-01") || values.dateRange[1].format("YYYY-MM-01"),
            end_date: maxDate?.format("YYYY-MM-31") || values.dateRange[1].format("YYYY-MM-31"),
            impact_dataset_id: confirmedDataset?.id,
            contract_type: "actual",
          };

          if (contract) {
            updateResidualWaste
              .mutateAsync({
                contractId: contract.id,
                contract: contractToPutPost,
              })
              .then((result) => {
                if (result.status === 200) {
                  analytics.track("Residual Waste Updated");
                  message.success("Residual waste updated.");
                  closeModal();
                }
              })
              .catch(() => message.error("Something went wrong while adding waste"));
          } else {
            createResidualWaste
              .mutateAsync({
                contract: contractToPutPost,
              })
              .then((result) => {
                if (result.status === 200) {
                  analytics.track("Residual Waste Added");
                  message.success("Residual waste added.");
                  closeModal();
                }
              })
              .catch(() => message.error("Something went wrong while adding waste"));
          }
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const validateReference = () => {
    if (!confirmedDataset) {
      return Promise.reject("No dataset selected");
    }
    return Promise.resolve();
  };

  return (
    <>
      <RootModal
        open={open}
        width={750}
        onCancel={() => closeModal()}
        okText={"Save"}
        keyboard={false}
        zIndex={998}
        title={"Add residual waste"}
        onOk={onSubmit}
        okButtonProps={{ loading: createResidualWaste.isLoading || updateResidualWaste.isLoading }}
      >
        <Form
          layout="vertical"
          form={form}
          name="residual_waste_form"
          initialValues={{
            unit: "public",
          }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[
              {
                required: true,
                message: "Name is required.",
              },
              {
                max: 100,
                message: "Name must be less than 100 characters.",
              },
            ]}
          >
            <RootInput
              showSkeleton={isFetching}
              width={"100%"}
            />
          </Form.Item>
          <Form.Item
            name="reference"
            rules={[
              {
                message: "Reference is required.",
                validator: validateReference,
              },
            ]}
          >
            <ImpactReferenceSelector
              confirmedDataset={confirmedDataset}
              selectedDataset={selectedDataset}
              onDatasetChange={onDatasetChange}
              onDatasetConfirm={onDatasetConfirm}
              onDelete={onDelete}
              loading={{ data: isFetching }}
              defaultDatabaseSearch={{ sector: "Waste Treatment & Recycling" }}
              defaultDatabaseFilter={{ special_activity_type: ["market activity"] }}
              modalTitle={"Add waste reference"}
            />
          </Form.Item>
          <Form.Item
            label="Amount"
            required={true}
          >
            <Space>
              <Form.Item
                name="amount"
                noStyle
                rules={[
                  {
                    required: true,
                    message: "Amount is required.",
                  },
                ]}
                style={{ marginBottom: 0 }}
              >
                <RootInputNumber
                  showSkeleton={isFetching}
                  width={"250px"}
                  addonAfter={selectedDataset?.unit}
                />
              </Form.Item>
              <ImpactPill
                impact={multiplyImpactWithFactor(selectedDataset?.impact, amount)}
                loading={isFetching}
              />
            </Space>
          </Form.Item>
        </Form>
      </RootModal>
    </>
  );
}
